import React, { ChangeEvent, useMemo, useState } from 'react';
import { Button, Dropdown, Header, Message, Modal } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import {
  importCreditoRealPolicies,
} from '../../../services/fire-insurance-policy';
import { dangerNotification, successNotification, warningNotification } from '../../../services/notification';

type EstateOption = {
  value: any;
  text: string;
};

type Props = {
  open: boolean;
  close: () => void;
  estates: EstateOption[];
  initialEstateId?: string;
  onSuccess: () => void;
};

type ImportFailure = {
  rowNumber: number;
  message: string;
};

type ImportSummary = {
  total: number;
  created: number;
  updated: number;
  failed: ImportFailure[];
};

const coverageNames = [
  'INCÊNDIO',
  'VIDROS',
  'RECOMPOSIÇÃO DOCS',
  'PERDA DE ALUGUEL',
  'ROUBO E SUBTRAÇÃO',
  'VENDAVAL'
];

const valueAt = (row: any[], index: number): any => row[index] ?? '';

const textValue = (value: any): string => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const numberValue = (value: any): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') return value;
  const string = String(value).trim();
  const normalized = string.includes(',')
    ? string.replace(/\./g, '').replace(',', '.')
    : string;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const dateValue = (value: any): string | undefined => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(
      value.getDate()
    ).padStart(2, '0')}`;
  }

  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }

  const string = textValue(value);
  const brazilianDate = string.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brazilianDate) {
    return `${brazilianDate[3]}-${brazilianDate[2]}-${brazilianDate[1]}`;
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(string) ? string : undefined;
};

const estateIdFromOption = (value: any): string => {
  if (value && typeof value === 'object') return String(value.id || '');
  return String(value || '');
};

const parseRows = (file: ArrayBuffer) => {
  const workbook = XLSX.read(file, { type: 'array', cellDates: true });
  const sheet = workbook.Sheets.Modelo;
  if (!sheet) throw new Error('A aba Modelo não foi encontrada.');
  const rows = XLSX.utils.sheet_to_json<any[]>(sheet, {
    header: 1,
    raw: true,
    defval: ''
  });

  return rows
    .slice(3)
    .map((row, index) => ({ row, rowNumber: index + 5 }))
    .filter(({ row }) => row.some(value => textValue(value)))
    .map(({ row, rowNumber }) => ({
      rowNumber,
      policyNumber: textValue(valueAt(row, 0)),
      policyId: textValue(valueAt(row, 2)),
      propertyExternalCode: textValue(valueAt(row, 37)),
      createdAt: dateValue(valueAt(row, 3)),
      startDate: dateValue(valueAt(row, 4)),
      endDate: dateValue(valueAt(row, 5)),
      validity: numberValue(valueAt(row, 6)?.toString().replace(/[^0-9]/g, '')),
      installments: numberValue(valueAt(row, 7)),
      installmentValue: numberValue(valueAt(row, 8)),
      status: textValue(valueAt(row, 9)),
      insurerName: textValue(valueAt(row, 10)),
      assistance: textValue(valueAt(row, 1)),
      address: {
        zipcode: textValue(valueAt(row, 23)),
        city: textValue(valueAt(row, 24)),
        state: textValue(valueAt(row, 25)),
        district: textValue(valueAt(row, 26)),
        street: textValue(valueAt(row, 27)),
        number: textValue(valueAt(row, 28))
      },
      rentValue: numberValue(valueAt(row, 29)),
      tenant: {
        name: textValue(valueAt(row, 30)),
        document: textValue(valueAt(row, 31)),
        email: textValue(valueAt(row, 32)),
        phone: textValue(valueAt(row, 33))
      },
      owner: {
        name: textValue(valueAt(row, 34)),
        document: textValue(valueAt(row, 35)),
        email: textValue(valueAt(row, 36))
      },
      coverages: coverageNames.map((name, coverageIndex) => ({
        name,
        value: numberValue(valueAt(row, 11 + coverageIndex * 2)),
        premium: numberValue(valueAt(row, 12 + coverageIndex * 2))
      }))
    }));
};

const CreditoRealImportModal = ({
  open,
  close,
  estates,
  initialEstateId,
  onSuccess
}: Props) => {
  const [estateId, setEstateId] = useState<string>(initialEstateId || '');
  const [rows, setRows] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);

  const options = useMemo(
    () => estates.map(option => ({ value: estateIdFromOption(option.value), text: option.text })),
    [estates]
  );

  const reset = () => {
    setRows([]);
    setFileName('');
    setIsLoading(false);
    setImportSummary(null);
    if (!initialEstateId) setEstateId('');
  };

  const handleClose = () => {
    reset();
    close();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = result => {
      try {
        const buffer = result.target?.result;
        if (!(buffer instanceof ArrayBuffer)) throw new Error('Arquivo inválido.');
        setRows(parseRows(buffer));
        setFileName(file.name);
      } catch (error) {
        setRows([]);
        dangerNotification('Erro', 'Não foi possível ler a planilha selecionada.');
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  const handleImport = async () => {
    if (!estateId) {
      warningNotification('Atenção', 'Selecione a imobiliária da importação.');
      return;
    }
    if (!rows.length) {
      warningNotification('Atenção', 'Selecione uma planilha com apólices.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await importCreditoRealPolicies({ estateId, policies: rows });
      setImportSummary(result);
      if (result.failed?.length) {
        warningNotification(
          'Importação concluída com pendências',
          `${result.created} criadas, ${result.updated} atualizadas e ${result.failed.length} com erro.`
        );
      } else {
        successNotification(
          'Sucesso',
          `${result.created} apólices criadas e ${result.updated} atualizadas.`
        );
      }
      onSuccess();
      if (!result.failed?.length) handleClose();
    } catch (error) {
      dangerNotification('Erro', 'Não foi possível importar as apólices.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} closeOnDimmerClick={false}>
      <Header icon="upload" content="Importar apólices Crédito Real" />
      <Modal.Content>
        <Dropdown
          fluid
          selection
          search
          options={options}
          value={estateId}
          placeholder="Selecione a imobiliária"
          onChange={(_, data) => setEstateId(String(data.value || ''))}
        />
        <div style={{ marginTop: 16 }}>
          <input id="credito-real-policy-file" type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
        </div>
        {fileName && (
          <p style={{ marginTop: 12 }}>
            {fileName}: {rows.length} linha(s) encontrada(s)
          </p>
        )}
        {importSummary && importSummary.failed.length > 0 && (
          <Message warning>
            <Message.Header>Linhas que não foram importadas</Message.Header>
            <div style={{ marginTop: 8, maxHeight: 180, overflowY: 'auto' }}>
              {importSummary.failed.map(failure => (
                <div key={`${failure.rowNumber}-${failure.message}`}>
                  Linha {failure.rowNumber}: {failure.message}
                </div>
              ))}
            </div>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button primary loading={isLoading} disabled={isLoading} onClick={handleImport}>
          Importar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CreditoRealImportModal;
